import { FormControlLabel, RadioGroup } from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import moment from "moment";
import * as React from "react";
import * as Yup from "yup";
import { t } from "../../../i18n/util";
import { hrStore } from "../../../stores/HrStore";
import { IApplicationStateEnum, IPostApplicationStateTransitionPayload, JobCategoryEnum } from "../../../types/models";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { CustomDatePicker } from "../../ui/CustomDatePicker";
import { CustomRadio } from "../../ui/CustomRadio";
import { ErrorMessagesList, FormButtons } from "../../ui/Primitives";
import { templateToString, transitionToTemplate } from "../../util/helpers";
import { authStore } from "../../../stores/AuthStore";

type Props = {
    onSubmit: (model: IPostApplicationStateTransitionPayload) => void;
    onBack: () => void;
    setHeaderField: React.Dispatch<React.SetStateAction<IApplicationStateEnum | undefined>>;
    firstName: string;
    fromApplicationState: IApplicationStateEnum;
    jobCategory?: JobCategoryEnum;
};

export const KanbanTransferDeniedDialog = ({
    onSubmit,
    onBack,
    setHeaderField,
    firstName,
    fromApplicationState,
    jobCategory
}: Props) => {
    const isMobile = useMobileMediaQuery();

    return (
        <Formik
            initialValues={hrStore.currentDialogForm}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape({
                evidence: Yup.boolean(),
                date: Yup.date().nullable().min(moment().toDate(), t("error.date")).typeError(t("error.date"))
            })}
            validateOnChange
            validateOnBlur
        >
            {({ setFieldValue, values, errors, touched }) => (
                <Form
                    style={{
                        flexGrow: 1,
                        display: "flex",
                        padding: 24,
                        flexDirection: "column",
                        gap: 16
                    }}
                >
                    <RadioGroup
                        name="evidence"
                        value={values.evidence}
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-start",
                            gap: 16
                        }}
                    >
                        <FormControlLabel
                            control={<Field component={CustomRadio} name="evidence" value="true" />}
                            label={t("screen.dashboard.dialog.rejectWithEvidence")}
                            style={{ height: 24 }}
                            onClick={() => {
                                setHeaderField("evidence");
                                setFieldValue(
                                    "template",
                                    transitionToTemplate(fromApplicationState, "evidence", jobCategory)
                                );
                                setFieldValue(
                                    "message",
                                    templateToString(
                                        values.language,
                                        transitionToTemplate(fromApplicationState, "evidence", jobCategory),
                                        { firstName, userName: authStore.givenName ?? "" }
                                    )
                                );
                            }}
                        />
                        <FormControlLabel
                            control={<Field component={CustomRadio} name="evidence" value="false" />}
                            label={t("screen.dashboard.dialog.rejectWithoutEvidence")}
                            style={{ height: 24 }}
                            onClick={() => {
                                setHeaderField("denied");
                                setFieldValue(
                                    "template",
                                    transitionToTemplate(fromApplicationState, "denied", jobCategory)
                                );
                                setFieldValue(
                                    "message",
                                    templateToString(
                                        values.language,
                                        transitionToTemplate(fromApplicationState, "denied", jobCategory),
                                        { firstName, userName: authStore.givenName ?? "" }
                                    )
                                );
                            }}
                        />
                    </RadioGroup>
                    <Field
                        component={CustomDatePicker}
                        name="date"
                        label={t("screen.dashboard.dialog.dateReturnFromEvidence")}
                        disablePast
                        disabled={values.evidence === "false"}
                        style={{ width: "100%", marginBottom: isMobile ? 10 : 32 }}
                    />
                    <ErrorMessagesList errors={errors} touched={touched} />
                    <FormButtons
                        labelBack={t("screen.dataUpload.dialog.cancel")}
                        labelSubmit={t("screen.motivation.next")}
                        onBack={onBack}
                        style={{
                            justifyContent: "flex-end",
                            marginBottom: 0
                        }}
                    />
                </Form>
            )}
        </Formik>
    );
};
